import Konva from 'konva';
import type { KonvaEventObject } from 'konva/lib/Node';
import { useCallback, type MouseEvent as ReactMouseEvent } from 'react';
import { useStudio } from '../../context';

export const useClick = (layer: Konva.Layer | null) => {
  const { editItem, items, selectedIds, selectedItems, selectItems } = useStudio();

  const onContainerClick = useCallback(
    (event: ReactMouseEvent<HTMLDivElement>) => {
      const nodeName = (event.target as HTMLElement).nodeName;
      if (nodeName === 'CANVAS' || nodeName === 'TEXTAREA') return;
      selectItems([]);
      editItem();
    },
    [editItem, selectItems],
  );

  const onStageClick = useCallback(
    (event: KonvaEventObject<MouseEvent>) => {
      if (!layer) return;

      if (event.target instanceof Konva.Stage) {
        selectItems([]);
        editItem();
        return;
      }

      // Do nothing if NOT clicked on a node
      if (!(event.target instanceof Konva.Node)) return;

      const metaPressed = event.evt.shiftKey || event.evt.ctrlKey || event.evt.metaKey;
      const targetId = event.target.id();
      const isSelected = selectedIds.has(targetId);

      // If no key pressed and the node is not selected, select just one
      if (!metaPressed && !isSelected) {
        selectItems([targetId]);
      }

      // If we pressed keys and node was selected we need to remove it from selection
      if (metaPressed && isSelected) {
        selectItems([...selectedIds].filter((x) => x !== targetId));
      }

      // Add the node into selection (skip locked items for multi-select)
      if (metaPressed && !isSelected) {
        const targetItem = items.find((x) => x.id === targetId);
        if (targetItem?.locked) return;
        const unlockedSelected = selectedItems.filter((x) => !x.locked).map((x) => x.id);
        selectItems([...unlockedSelected, targetId]);
      }

      layer.batchDraw();
    },
    [editItem, items, layer, selectItems, selectedIds, selectedItems],
  );

  return { onContainerClick, onStageClick };
};
